/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 10-09-2026
 *
 * De teacher-replay (golf a5): een puur lokale weergave van de opgeslagen snapshots van een model.
 * De service houdt de stappenstand (welke stap, hoeveel er zijn) en de zes ingangen die de balk en
 * de toolbar aanroepen. Hij bezit geen tekenwerk of opslag zelf; hij stuurt de bestaande diensten
 * aan: `DlModel.tekenGraaf` tekent de stap, de baas past de simulatiestand toe en zet de simview op
 * slot, de opslagketen levert de save vooraf en de herlading achteraf.
 *
 * De stappen komen per stuk binnen: bij het openen de lijst id's (`haalSnapshotlijst`), daarna per
 * klik één snapshot (`haalSnapshot`), met de buurstappen vooruit opgehaald en een cache op id. Elk
 * antwoord wordt bij aankomst getoetst — nog actief, nog hetzelfde model, nog het laatste
 * volgnummer — zodat een laat of achterhaald antwoord de weergave niet overschrijft.
 *
 * Sinds stap 2 mag terugbladeren ook tijdens een samenwerking. Het slot van 10-08-2026 is vervangen
 * door een gemelde afwezigheid: de registratie draagt de replayvlag, waarna het backend de leiding
 * doorschuift naar iemand die wél kan muteren. Het binnengaan en het verlaten zijn daarmee elk een
 * vaste volgorde geworden; zie `initReplay` en `stop`.
 */
import {computed, inject, Injectable, signal} from '@angular/core';
import {Sihwlog} from 'sihw-ngx';
import {ApiService} from '../backend/api.service';
import type {ModelId, Replaystap} from '../backend/api.service';
import {OpslagService} from '../model/opslag.service';
import type {Modelsnapshot} from '../model/opslag.service';
import {DlModel} from '../model/dl-model';
import {GlobalModelcanvasService} from '../model/global-modelcanvas.service';
import {NormService} from '../engines/norm.service';
import type {BewaardeNormstand, WerkModelData} from '../engines/norm.service';
import {BouwsuggestieEngineService} from '../engines/bouwsuggestie-engine.service';
import {SamenwerkprotocolService} from '../samenwerking/samenwerkprotocol.service';
import type {SimulatieStatus} from '../samenwerking/samenwerkprotocol.service';
import {SimulatieStatusService} from '../../services/simulatie-status.service';
import {SimulatieService} from '../simulatie/simulatie.service';
import {FeedbackSelectieService} from '../../services/feedback-selectie.service';
import type {BouwsuggestieSoort} from '../../services/feedback-selectie.service';
import {ToastService} from '../../services/toast.service';

/**
 * Hoe lang we bij het binnengaan hoogstens op de opslagketen wachten. Twee momenten gebruiken hem:
 * de opslagronde van de laatste actie (die zelf de gedebouncete bouwsuggestie-run afwacht) en de
 * snapshots die nog onderweg zijn naar het backend. Loopt hij af, dan gaat de replay wél door — dan
 * mist hij hoogstens zijn allerlaatste stap, en dat is beter dan een knop die niets doet.
 */
const WACHTLIMIET_MS = 3000;

@Injectable({providedIn: 'root'})
export class ReplayService {

    private readonly log = new Sihwlog('info', 'ReplayService');

    /**
     * De backendlaag: de lijst met snapshot-id's en de snapshots zelf.
     */
    private readonly api = inject(ApiService);

    /**
     * De opslagketen: of het model opgeslagen is, de save vooraf, de buffer en de harde herlading.
     */
    private readonly opslag = inject(OpslagService);

    /**
     * Het document: de replayvlag, de bewerkblokkade en het tekenen van een stap.
     */
    private readonly dlModel = inject(DlModel);

    /**
     * De actieve `cy` van het modelcanvas, om hem tijdens de replay op slot te zetten.
     */
    private readonly globalModelcanvas = inject(GlobalModelcanvasService);

    /**
     * De samenwerk-baas: het toepassen van de simulatiestand per stap, de simview readonly, en de
     * bouwsuggestie-run bij de save vooraf.
     */
    private readonly samenwerkprotocol = inject(SamenwerkprotocolService);

    /**
     * De simulatiestatus: het open- en dichtdoen van het simulatiescherm per stap.
     */
    private readonly simulatieStatus = inject(SimulatieStatusService);

    /**
     * De simulatieaansturing: bij het terugkeren vraagt hij welke invoerhash er nu bij dit model
     * hoort, zodat we weten of de bewaarde simulatie nog geldt.
     */
    private readonly simulatie = inject(SimulatieService);

    /**
     * De norm: hij zet de normstand van een stap terug, en als een snapshot niet op tijd geschreven
     * is breken we zijn ronde af met een generatiebump, zodat er tijdens de replay geen uitslag meer
     * binnen kan vallen.
     */
    private readonly normService = inject(NormService);

    /**
     * De bouwsuggestie-engine: hij neemt de gesnapshotte run van een stap over. Draaien doet hij
     * tijdens een replay niet — die guard zit in de baas.
     */
    private readonly bouwsuggestieEngine = inject(BouwsuggestieEngineService);

    /**
     * De feedbackselectie: de aanwijzing die bij een stap bewaard is, komt hier terug.
     */
    private readonly feedbackSelectie = inject(FeedbackSelectieService);

    /**
     * De meldingen: een replay die niet doorgaat zegt dat, in plaats van stil niets te doen.
     */
    private readonly toast = inject(ToastService);

    /**
     * De id's van de stappen, oudste eerst. Het backend levert ze nieuwste eerst; hier staan ze
     * omgedraaid zodat `huidige` gewoon oploopt van begin naar eind, net als de balk telt.
     */
    private stappen: number[] = [];

    /**
     * Het model waarvan de replay loopt, vastgelegd bij het openen. Een antwoord dat binnenkomt voor
     * een ander model wordt genegeerd.
     */
    private replaymodel: ModelId | null = null;

    /**
     * De opgehaalde stappen, op id. Bladeren blijft daarmee vloeiend en een tweede bezoek aan een
     * stap kost geen call.
     */
    private readonly cache = new Map<number, Replaystap>();

    /**
     * Het volgnummer van de laatste aanvraag. Klikt de gebruiker sneller dan het backend antwoordt,
     * dan tekent alleen het antwoord met het hoogste nummer; oudere antwoorden vallen af.
     */
    private aanvraagteller = 0;

    /**
     * De drie weergavevoorkeuren zoals ze stonden voordat de replay begon. Elke stap zet ze naar de
     * stand van toen (`zetVoorkeuren`), en de synchrone hertekening bij het verlaten leest ze uit de
     * `Documentstand` — die de voorkeuren niet bevat. Zonder deze herinnering blijf je dus achter met
     * het modelleerniveau van de laatste replaystap.
     */
    private voorkeurenVooraf: {modelleerniveau: number; simulatiepreferentie: number; fastest_path: boolean | null} | null = null;

    /**
     * De simulatie zoals hij draaide voordat de replay begon, met de invoerhash waar hij bij hoort.
     * `null` als er niet gesimuleerd werd. Het opruimen bij elke replaystap is bewust — wat er dan
     * staat is de stand van tóen, en die mag de positie-herinnering van je eigen laatste simulatie
     * niet overschrijven — maar dan moet die eigen stand wel ergens bewaard zijn.
     */
    private simulatieVooraf: {stand: SimulatieStatus; invoerhash: string | null} | null = null;

    /**
     * De normstand zoals hij lag voordat de replay begon, met de modelstand waar hij bij hoort. Elke
     * replaystap zet zijn eigen normstand neer. Het stoppen zet deze terug, zodat de herlading daarna
     * niet opnieuw rekent op een model dat niet veranderd is. Zie `NormService.bewaarStand`.
     */
    private normVooraf: BewaardeNormstand | null = null;

    private readonly _actief = signal(false);
    private readonly _huidige = signal(0);
    private readonly _aantal = signal(0);

    /**
     * Of er een replay loopt. De balk hangt hieraan voor zijn zichtbaarheid.
     */
    public readonly actief = this._actief.asReadonly();

    /**
     * De huidige stap (0-based), oplopend van begin naar eind.
     */
    public readonly huidige = this._huidige.asReadonly();

    /**
     * Het aantal stappen in de replay.
     */
    public readonly aantal = this._aantal.asReadonly();

    /**
     * Of we op de eerste stap staan (dan kan er niet verder terug).
     */
    public readonly eerste = computed(() => this._huidige() === 0);

    /**
     * Of we op de laatste stap staan (dan kan er niet verder vooruit).
     */
    public readonly laatste = computed(() => this._huidige() === this._aantal() - 1);

    constructor() {
        //een modelwissel of reset tijdens een replay breekt hem af; de opslagketen laat de
        //bewerkblokkade daarbij zelf al los (ontwerpvraag 9)
        this.opslag.bijGereset(() => this.breekAf());
    }

    /**
     * Opent de replay en toont de laatste stap. Aangeroepen door de toolbarknop.
     */
    public async toon(): Promise<void> {
        if (!await this.initReplay()) {
            return;
        }
        await this.gaNaar(this.stappen.length - 1);
    }

    /**
     * Naar de eerste stap.
     */
    public async naarBegin(): Promise<void> {
        if (!this._actief()) {
            return;
        }
        await this.gaNaar(0);
    }

    /**
     * Eén stap terug.
     */
    public async terug(): Promise<void> {
        if (!this._actief() || this.eerste()) {
            return;
        }
        await this.gaNaar(this._huidige() - 1);
    }

    /**
     * Eén stap verder.
     */
    public async verder(): Promise<void> {
        if (!this._actief() || this.laatste()) {
            return;
        }
        await this.gaNaar(this._huidige() + 1);
    }

    /**
     * Naar de laatste stap.
     */
    public async naarEind(): Promise<void> {
        if (!this._actief()) {
            return;
        }
        await this.gaNaar(this.stappen.length - 1);
    }

    /**
     * Stopt de replay. De volgorde is dwingend, want het canvas mag geen replaystand tonen op het
     * moment dat er weer gemuteerd en opgeslagen kan worden: een save leest het canvas en niet de
     * `Documentstand`, en een replaystap staat alleen op dat canvas.
     *
     * 1. de simulatie opruimen — opruimen en niet resetten, zodat de replaystand de
     *    positie-herinnering van de eigen laatste simulatie niet overschrijft. Het moet vóór de
     *    hertekening, anders lopen de suggestielampjes van de laatste stap mee het levende model in;
     * 2. de weergavevoorkeuren en de normstand van vóór de replay terug, en synchroon hertekenen uit de
     *    `Documentstand` — die stand bezit de opslagketen al, dus dit kost geen backendcall;
     * 3. dán pas de blokkade en de vlag eraf, en het canvas en de simview weer vrij;
     * 4. dán de verse stand uit het backend, met aanleiding `herstel`: de norm en de bouwsuggesties
     *    horen niet te pulseren alsof de fouten van zojuist nieuw zijn. Mislukt dit, dan staat er nog
     *    steeds een geldig model op het canvas. Is het model niet veranderd, dan rekent de norm daarbij
     *    niet opnieuw: de teruggezette normstand hoort bij precies die stand. Een nieuwe ronde kon door
     *    de montecarlo-zoektocht een fout op een andere node leggen, en dan was het screenshot erna een
     *    stap die niemand zette (test 6 van test-golf-a5.md).
     *
     * Die herlading registreert onderweg zelf opnieuw, en dat is meteen de afmelding van de
     * replayvlag; landt hij niet, dan doen we die registratie alsnog met de hand. Een volger vraagt
     * daarna zijn leider om een volledige bijwerking: hij rekent zelf niets, en bij een
     * herregistratie op hetzelfde model stuurt de leider uit zichzelf niets.
     *
     * Als laatste komt je eigen simulatie terug, als het model tenminste nog hetzelfde is; zie
     * `herstelSimulatie`.
     */
    public async stop(): Promise<void> {
        if (!this._actief()) {
            return;
        }
        void this.samenwerkprotocol.pasSimulatiestatusToe(null); //zet ook `simuleren` uit; opruimen gebeurt meteen
        this.herstelVoorkeuren();
        //nog in readonly: het terugzetten logt en verstuurt dan niets
        if (this.normVooraf) {
            this.normService.herstelStand(this.normVooraf);
            this.normVooraf = null;
        }
        await this.opslag.herlaad('herstel');
        this.dlModel.laatBewerkenLos();
        this.dlModel.zetInReplay(false);
        this.zetCanvasOpSlot(false);
        this.ruimStandOp();
        const herladen = await this.opslag.herlaadUitBackend('herstel');
        if (!herladen) {
            await this.samenwerkprotocol.registreerSamenwerken(); //de vlag moet er hoe dan ook af
        }
        this.samenwerkprotocol.vraagSync();
        await this.herstelSimulatie(); //ná de herlading: pas dan is bekend of het nog hetzelfde model is
        //het readonly is voorbij, en als leider of lokaal hoort daar een snapshot bij. Pas hier, en alleen
        //na een geslaagde herlading: die zet de normronde van het levende model in gang, en het
        //screenshot hoort op die uitslag te wachten. Zonder herlading droeg hij de normstand van de
        //laatste replaystap. Meestal is hij gelijk aan de laatste stap, en dan houdt het backend hem tegen
        if (herladen) {
            this.opslag.markeerSnapshotNodig();
        }
    }

    /**
     * Regelt de replay zodra dat nodig is, in de volgorde die golf a5 vastlegt:
     *
     * 0. de opslagronde van de laatste actie afwachten. Die wacht zelf eerst de gedebouncete
     *    bouwsuggestie-run af; stond de blokkade er al als hij wakker werd, dan sloeg hij over en
     *    miste de replay precies de stap die je zojuist maakte;
     * 1. de replayvlag zetten en het canvas op slot. Vanaf hier zijn beide dispatchers en de
     *    klikroutes eromheen dicht, dus levert een klik geen logregel, geen bericht en geen normronde
     *    meer op;
     * 2. openstaand werk wegschrijven met een directe, **niet-relevante** save. Niet-relevant is
     *    wezenlijk: een relevante save start een normronde, en dan zou elke replaystart de laatste
     *    actie zijn feedback kosten. Er valt ook niets te herrekenen — we schrijven alleen de buffer
     *    weg. Mislukt de save, dan gaat de replay niet door: de herlading bij het stoppen zou de
     *    buffer legen en dan is dat werk kwijt;
     * 3. de snapshots afwachten die nog onderweg zijn. Loopt de limiet af, dan de normworker stoppen,
     *    zodat er daarna geen uitslag meer binnen kán vallen;
     * 4. de bewerkblokkade erop en opnieuw registreren — nu met de replayvlag, waarna het backend de
     *    leiding doorschuift naar iemand die wél kan muteren;
     * 5. de stappenlijst ophalen. Is die leeg of mislukt de call, dan draaien we alles terug,
     *    inclusief een registratie zonder vlag, en melden we dat.
     *
     * Geeft terug of de replay draait.
     */
    private async initReplay(): Promise<boolean> {
        if (this._actief()) {
            return true;
        }
        if (!this.opslag.isOpgeslagen()) {
            return false;
        }
        const model = this.opslag.modelId();
        if (!model) {
            return false;
        }
        if (!await this.samenwerkprotocol.wachtOpOpslagronde(WACHTLIMIET_MS)) {
            this.log.warn('de opslagronde van de laatste actie was niet op tijd klaar');
        }
        this.onthoudVoorkeuren();
        this.dlModel.zetInReplay(true);
        this.zetCanvasOpSlot(true);
        if (!await this.schrijfWerkWeg()) {
            this.dlModel.zetInReplay(false);
            this.zetCanvasOpSlot(false);
            return false;
        }
        //ná de save, want de versie die we hier bewaren is het ijkpunt voor de terugkeer. Vergelijken
        //met de versie tijdens de replay kan niet: die staat op 0 zodra er een bericht binnenkwam
        this.onthoudSimulatie();
        if (!await this.opslag.wachtOpSnapshots(WACHTLIMIET_MS)) {
            //de normronde eraf: een afgebroken ronde telt als rust met wat er ligt, dus daarna nog één
            //korte ronde om de snapshot weg te laten gaan. Hij kan ook op de wachtdrempel gestaan hebben
            this.log.warn('een snapshot was niet op tijd geschreven; de normronde gaat eraf');
            this.normService.stopWorker();
            await this.opslag.wachtOpSnapshots(WACHTLIMIET_MS);
        }
        //de normstand van nu, met de modelstand die bij het stoppen uit het backend terugkomt: de save
        //hierboven heeft precies die stand weggeschreven. Rekent de norm nog, dan hoort er geen stand bij
        //en rekent het stoppen gewoon opnieuw
        this.normVooraf = this.normService.bewaarStand(this.opslag.ruweData().model as unknown as WerkModelData);
        this.dlModel.blokkeerBewerken();
        await this.samenwerkprotocol.registreerSamenwerken();
        const ids = await this.haalLijst(model);
        if (!ids?.length) {
            this.log.warn('replay gaat niet door: geen stappen');
            this.dlModel.laatBewerkenLos();
            this.dlModel.zetInReplay(false);
            this.zetCanvasOpSlot(false);
            await this.samenwerkprotocol.registreerSamenwerken(); //nu weer zonder de replayvlag
            this.toast.toon('REPLAY.NIET_GELUKT');
            return false;
        }
        this.replaymodel = model;
        this.stappen = [...ids].reverse(); //backend geeft nieuwste eerst; wij tellen oplopend
        this._aantal.set(this.stappen.length);
        this.cache.clear();
        this._actief.set(true);
        return true;
    }

    /**
     * Schrijft openstaand werk weg voordat de replay begint, zodat de laatste actie nog een stap
     * wordt. Staat er niets in de buffer en is er ook geen save gepland, dan slaan we niet op maar
     * wachten we alleen een lopende save af: een save met een lege buffer bumpt de versie en stuurt
     * de volgers een `modelchanged` voor niets. Geeft terug of we door kunnen.
     */
    private async schrijfWerkWeg(): Promise<boolean> {
        if (!this.opslag.actionbuffer().length && !this.opslag.saveGepland) {
            await this.opslag.onNotSaving();
            return true;
        }
        return (await this.opslag.save(this.dlModel.modelData(), false, true)) !== false;
    }

    /**
     * Zet het canvas en de simview op slot of haalt ze er weer af. Gaat samen met de replayvlag en
     * niet met de bewerkblokkade: het houdt de directe canvasroutes (slepen, selecteren) uit het
     * venster waarin de vlag al staat en de blokkade nog niet.
     */
    private zetCanvasOpSlot(opSlot: boolean): void {
        const cy = this.globalModelcanvas.getCy();
        cy?.autolock(opSlot);
        cy?.autounselectify(opSlot);
        this.samenwerkprotocol.zetSimviewReadonly(opSlot);
    }

    /**
     * Legt de simulatie vast zoals hij nu draait, met de invoerhash waar hij bij hoort. Draait er
     * geen simulatie, dan valt er ook niets terug te zetten. De simulatie zelf gaat mee in deze
     * bewaarde stand: dan hoeft hij bij het terugkeren niet opnieuw opgehaald te worden.
     */
    private onthoudSimulatie(): void {
        const stand = this.samenwerkprotocol.haalSimulatiestatus(true, true);
        this.simulatieVooraf = stand.simuleren ? {stand, invoerhash: stand.invoerhash ?? null} : null;
    }

    /**
     * Zet je eigen simulatie terug na afloop van de replay, maar alleen als het model nog hetzelfde
     * is. De harde herlading bij het stoppen kan een verse stand opleveren — bij samenwerking is dat
     * het normale geval — en dan hoort een simulatie van vóór die wijziging er niet meer bij. Dan
     * blijft het bij opruimen, precies zoals het was.
     *
     * Er is geen losse `zetSimuleren(true)` nodig: `pasSimulatiestatusToe` opent het scherm zelf, als
     * eerste, en dat moet ook — anders mislukt het passend maken. De herkomst is `teruggehaald`: dit
     * is de simulatie die je al gezien had, dus er valt niets nieuws te melden en de ijklijn van de
     * simfeedback blijft staan waar hij stond.
     */
    private async herstelSimulatie(): Promise<void> {
        const bewaard = this.simulatieVooraf;
        this.simulatieVooraf = null;
        if (!bewaard) {
            return;
        }
        //op de invoerhash en niet op de modelversie (golf 3.6, kernbesluit 13): die versie loopt ook op
        //als een medewerker alleen iets verschoof, en dan raakte je je simulatie kwijt terwijl de
        //prolog geen letter veranderde. De hash zegt precies wat telt — dezelfde invoer, dezelfde
        //simulatie. Bij een cache-treffer is dit één goedkope call zonder runner
        const nu = await this.simulatie.geldigeInvoerhash();
        if (!bewaard.invoerhash || nu !== bewaard.invoerhash) {
            this.log.debug('de simulatie komt niet terug: de invoer van het model is intussen gewijzigd');
            return;
        }
        await this.samenwerkprotocol.pasSimulatiestatusToe(bewaard.stand, true, 'teruggehaald');
    }

    /**
     * Legt de drie weergavevoorkeuren vast zoals ze nu staan, zodat het verlaten ze terug kan zetten.
     */
    private onthoudVoorkeuren(): void {
        this.voorkeurenVooraf = {
            modelleerniveau: this.dlModel.modelleerniveau,
            simulatiepreferentie: this.dlModel.simulatiepreferentie,
            fastest_path: this.dlModel.fastest_path
        };
    }

    /**
     * Zet de weergavevoorkeuren terug op de stand van vóór de replay.
     */
    private herstelVoorkeuren(): void {
        if (this.voorkeurenVooraf) {
            this.opslag.setProperties(this.voorkeurenVooraf);
        }
    }

    /**
     * Haalt de stappenlijst op; een mislukte call laat de replay niet doorgaan.
     */
    private async haalLijst(model: ModelId): Promise<number[] | null> {
        try {
            return await this.api.haalSnapshotlijst(model);
        } catch (fout) {
            this.log.warn('replay-stappenlijst ophalen mislukt', fout);
            return null;
        }
    }

    /**
     * Zet de huidige stap en tekent hem.
     */
    private async gaNaar(index: number): Promise<void> {
        this._huidige.set(index);
        await this.tekenStap(index);
    }

    /**
     * Tekent de stap op het opgegeven index. Het antwoord tekent alleen als de replay nog actief is,
     * het model nog klopt en dit nog de laatste aanvraag is; anders heeft de gebruiker intussen
     * doorgeklikt of gestopt.
     *
     * De volgorde binnen een stap ligt vast, want de latere delen wissen de eerdere: tekenen →
     * simulatiestand → normstand → bouwsuggesties → aanwijzing. De tekenmelding wist elke selectie en
     * de simulatiestand doet dat ook, dus de aanwijzing komt als laatste; de bouwsuggesties moeten ná
     * het tekenen, anders haalt de automatische ronde die aan de hertekening hangt ze weg.
     *
     * Alle drie de feedbackblokken komen binnen als `teruggehaald`. Daarmee is in de data geregeld
     * dat er niets pulseert: geen nieuwe foutsleutels, geen nieuwe suggestieregels en een ijklijn die
     * blijft staan. Er staat dus nergens in de weergave een replay-guard.
     */
    private async tekenStap(index: number): Promise<void> {
        const id = this.stappen[index];
        const seq = ++this.aanvraagteller;
        const stap = await this.haalStap(id);
        if (!this._actief() || seq !== this.aanvraagteller || this.opslag.modelId() !== this.replaymodel) {
            return;
        }
        if (!stap) {
            return;
        }
        const snapshot = stap.snapshot as Modelsnapshot;
        this.zetVoorkeuren(stap.meta);
        //teken zonder rezoom: de uitsnede van de vorige stap blijft staan
        await this.dlModel.tekenGraaf(snapshot, 'replaystap');
        //de aanwijzing van de vórige stap moet hier weg, en nadrukkelijk vóór de simulatiestand: die
        //zet zijn eigen aanwijzing terug, en een wis erná haalde hem meteen weer onderuit
        this.feedbackSelectie.wisSelectie();
        //de simulatiestand: de baas tekent de canvasstand en past de simfeedback toe die in dezelfde
        //status meereist; hij zendt en logt niet, dus dit is replayveilig. Het simscherm moet vóór
        //het toepassen open staan, anders mislukt het passend maken
        //sinds 17-09-2026 draagt een nieuwe snapshot alleen de invoerhash; het toepassen haalt de
        //simulatie dan op, en valt terug op de vorige als opeenvolgende stappen dezelfde hash hebben
        const simstatus = snapshot.simstatus as SimulatieStatus | undefined;
        if (simstatus?.simuleren) {
            this.simulatieStatus.zetSimuleren(true);
            await this.samenwerkprotocol.pasSimulatiestatusToe(simstatus, true, 'teruggehaald');
            if (!this._actief() || seq !== this.aanvraagteller) {
                return; //de replay ging intussen verder of stopte
            }
        } else {
            this.simulatieStatus.zetSimuleren(false);
        }
        this.zetFeedbackstand(snapshot);
        this.prefetch(index);
    }

    /**
     * Zet de feedback van deze stap terug: de norm, de bouwsuggesties en de aanwijzing. Ontbreekt een
     * blok — een snapshot van vóór golf a5, of een stap waarvan de rekenronde het niet haalde — dan
     * gaat dat deel juist nadrukkelijk leeg. De normbalk dimt dan en er staan geen lampjes; de
     * cijfers van nu laten staan zou bij een andere stand horen.
     *
     * Het leegmaken van de vorige aanwijzing gebeurt in `tekenStap`, vóór de simulatiestand; hier
     * staat alleen het terugzetten. Een simulatie-aanwijzing is dan al gezet en blijft staan — de
     * drie sluiten elkaar uit, dus een snapshot draagt er hoogstens één.
     */
    private zetFeedbackstand(snapshot: Modelsnapshot): void {
        this.normService.zetReplaystand(snapshot.norm ?? null);
        this.bouwsuggestieEngine.importeerRun(snapshot.bouwsuggesties ?? [], 'teruggehaald');
        const selectie = snapshot.selectie;
        if (selectie?.fout) {
            this.feedbackSelectie.selecteerFout(selectie.fout.elementId, selectie.fout.foutcode, 'ontvangen');
        } else if (selectie?.bs) {
            this.feedbackSelectie.selecteerBouwsuggestie(selectie.bs.handle, selectie.bs.nodeId,
                this.soortVanSuggestie(selectie.bs.handle), 'ontvangen');
        }
    }

    /**
     * De soort van een bouwsuggestieregel op zijn handle. De snapshot bewaart de soort niet apart —
     * de aanwijzing is `{handle, nodeId}` — en de engine kent hem wel.
     */
    private soortVanSuggestie(handle: string): BouwsuggestieSoort {
        return this.bouwsuggestieEngine.soortVanRegel(handle) ?? 'noodzakelijk';
    }

    /**
     * Zet de weergavevoorkeuren van deze stap, maar alleen als ze afwijken van wat er nu staat. De
     * `fp` uit de meta is een aan/uit-waarde (`true` of `0`); we lezen hem als boolean.
     */
    private zetVoorkeuren(meta: Replaystap['meta']): void {
        const snelstePad = !!meta.fp;
        if (meta.mn !== this.dlModel.modelleerniveau
            || meta.sp !== this.dlModel.simulatiepreferentie
            || snelstePad !== this.dlModel.fastest_path) {
            this.opslag.setProperties({
                modelleerniveau: meta.mn,
                simulatiepreferentie: meta.sp,
                fastest_path: snelstePad
            });
        }
    }

    /**
     * Haalt één stap op, uit de cache of van het backend. Een mislukte call levert `null`.
     */
    private async haalStap(id: number): Promise<Replaystap | null> {
        const gecached = this.cache.get(id);
        if (gecached) {
            return gecached;
        }
        if (!this.replaymodel) {
            return null;
        }
        try {
            const stap = await this.api.haalSnapshot(this.replaymodel, id);
            this.cache.set(id, stap);
            return stap;
        } catch (fout) {
            this.log.warn('replaystap ophalen mislukt', id, fout);
            return null;
        }
    }

    /**
     * Haalt de buurstappen alvast op in de cache. Een mislukte of achterhaalde prefetch laat de
     * huidige weergave met rust: het resultaat wordt alleen bewaard, niet getekend.
     */
    private prefetch(index: number): void {
        for (const buur of [index - 1, index + 1]) {
            if (buur >= 0 && buur < this.stappen.length) {
                void this.haalStap(this.stappen[buur]);
            }
        }
    }

    /**
     * Breekt de replay af bij een modelwissel of reset: de vlag eraf, het canvas en de simview vrij,
     * de stappen weg. Géén `laatBewerkenLos()` — de opslagketen heeft dat bij de reset al gedaan — en
     * geen herlading, want de reset tekent zelf de nieuwe stand.
     */
    private breekAf(): void {
        if (!this._actief()) {
            return;
        }
        this.dlModel.zetInReplay(false);
        this.zetCanvasOpSlot(false);
        this.simulatieVooraf = null; //een ander model: die simulatie hoort er niet meer bij
        this.normVooraf = null; //en die normstand ook niet
        this.ruimStandOp();
    }

    /**
     * Zet de eigen stand terug op "geen replay": de vlag, de stappen, de cache, de onthouden
     * voorkeuren en het volgnummer. De bewaarde simulatie blijft hier bewust staan: `stop` ruimt de
     * stand op vóórdat hij herlaadt, en zet die simulatie daarna pas terug.
     */
    private ruimStandOp(): void {
        this._actief.set(false);
        this.stappen = [];
        this._aantal.set(0);
        this._huidige.set(0);
        this.cache.clear();
        this.replaymodel = null;
        this.voorkeurenVooraf = null;
        this.aanvraagteller++; //een antwoord dat nog onderweg is, tekent niet meer
    }
}
